import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import CountProductListTable from "./CountProductListTable";
import SpecBackButton from "../../components/SpecBackButton";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import inventoryClient from "../../clients/inventoryClient";
import SpecSpinner from "../../components/SpecSpinner";
import useVendorCurrency from "../../hooks/useVendorCurrency";
import Store, { activeRouteKeys } from "../../Store/Store";
import utilFunctions from "../../utilities/utilFunctions";
import inventoryObjects, {
  inventoryCountTypesMap,
} from "../../clients/inventoryObjects";
import CreateMissingCostsModal from "../../components/Modals/CreateMissingCostsModal";
import MissingParLevelsModal from "../../components/Modals/MissingParLevelsModal";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import OrderToParConfirmationModal from "../../components/Modals/OrderToParConfirmationModal";
import OrderToParSuccessModal from "../../components/Modals/OrderToParSuccessModal";
import OrderToParNoValidOrdersModal from "../../components/Modals/OrderToParNoValidOrdersModal";
import EditStockCountRecordQuantityModal from "../../components/Modals/EditStockCountRecordQuantityModal";
import AddProductToCompletedStockCountModal from "../../components/Modals/AddProductToCompletedStockCountModal";
import SnackBarSuccess from "../../components/SnackBarSuccess";

const StockCountReview = () => {
  const [costsModalOpen, toggleCostsModalOpen] = useState(false);
  const [parsModalOpen, setParsModalOpen] = useState(false);
  const [recordForCostSetting, setRecordForCostSetting] = useState(null);
  const [editQuantityModalOpen, setEditQuantityModalOpen] = useState(false);
  const [recordForQuantityEdit, setRecordForQuantityEdit] = useState(null);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [alertSuccess, setAlertSuccess] = useState(undefined);
  const [filter, setFilter] = useState(false);
  const [showOrderConfirmationModal, toggleOrderConfirmationModal] =
    useState(false);
  const [noProductsUnderPar, toggleNoUnderParProducts] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [showSuccessOrderToParModal, toggleSuccessOrderToParModal] =
    useState(false);
  const [orderToParError, setOrderToParError] = useState(false);
  const [showNoValidOrdersModal, toggleNoValidOrdersModal] = useState(false);
  const [searchParams] = useSearchParams();
  const store = useContext(Store);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { establishmentid: establishmentId, stockcountid: stockCountId } =
    useParams();
  const { currencySymbol, currency, locale } = useVendorCurrency(
    store.user?.country
  );
  const { isLoading, data } = useQuery(
    ["stock-count", { establishmentId, stockCountId }],
    inventoryClient.getStockCountById
  );

  const handleCostsModalOpen = (record) => {
    setRecordForCostSetting(record);
    toggleCostsModalOpen(true);
  };

  const handleClostCostModal = () => {
    setRecordForCostSetting(null);
    toggleCostsModalOpen(false);
  };

  const handleParsModalOpen = () => {
    setParsModalOpen(!parsModalOpen);
  };

  const handleEditQuantityModalOpen = (record) => {
    setRecordForQuantityEdit(record);
    setEditQuantityModalOpen(true);
  };

  const handleCloseEditQuantityModal = () => {
    setRecordForQuantityEdit(null);
    setEditQuantityModalOpen(false);
  };

  const handleAddProductModalOpen = () => {
    setAddProductModalOpen(true);
  };

  const handleCloseAddProductModal = () => {
    setAddProductModalOpen(false);
  };

  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
  };

  const handleOpenSortMenu = (e) => {
    setSortMenuAnchor(e.currentTarget);
  };

  const setUnderFilter = () => {
    if (filter === "underPar") {
      setFilter(false);
      handleCloseSortMenu();
      return;
    }
    setFilter("underPar");
    handleCloseSortMenu();
  };

  const setNoParFilter = () => {
    if (filter === "noPar") {
      setFilter(false);
      handleCloseSortMenu();
      return;
    }
    setFilter("noPar");
    handleCloseSortMenu();
  };

  const handleCloseOrderToParModal = () => {
    toggleOrderConfirmationModal(false);
    toggleNoUnderParProducts(false);
  };

  const handleOrderToPar = (confirmationGiven) => {
    handleCloseOrderToParModal();
    const hasUnderPar = stockCount?.stockCountRecords.some(
      (record) =>
        record.finalParStatus === inventoryObjects.parStatusTypes.under
    );

    if (!hasUnderPar) {
      toggleNoUnderParProducts(true);
      return;
    }

    orderToParMutation.mutate({
      establishmentId,
      countId: stockCountId,
      confirmationGiven: confirmationGiven === true ? true : false,
    });
  };

  useEffect(() => {
    store.updateActiveRoute(
      activeRouteKeys.stockCount,
      `spec/${establishmentId}/count-review/${stockCountId}`
    );
  }, []);

  const orderToParMutation = useMutation(inventoryClient.orderToPar, {
    onSuccess: (res) => {
      const resData = res.data;
      setOrderToParError(false);
      if (resData.needsConfirmation) {
        setConfirmationData(resData);
        toggleOrderConfirmationModal(true);
        return;
      } else if (resData.noOrdersToCreate) {
        setConfirmationData(resData);
        toggleNoValidOrdersModal(true);
        return;
      }

      toggleSuccessOrderToParModal(true);
    },
    onError: (error) => {
      console.error("Error ordering to par:", error);
      setOrderToParError(true);
    },
  });

  const showCongrats = searchParams.get("congrats") != null;

  if (isLoading) {
    return <SpecSpinner open text={"Loading Review..."} />;
  }

  const stockCount = data.stockCount;
  return (
    <>
      <Grid
        container
        direction="column"
        sx={{
          maxWidth: "2440px",
          padding: 4,
          marginTop: 6,
        }}
      >
        <Box>
          <SpecBackButton
            backText="Stock Count"
            onClick={() => navigate(`/spec/${establishmentId}/stockcount`)}
          />
        </Box>
        <Box sx={{ marginTop: 6 }}>
          <Typography variant="subtitle1">
            {inventoryCountTypesMap[stockCount.type]}
          </Typography>
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h2">{stockCount.name}</Typography>
        </Box>
        {orderToParError ? (
          <Alert severity="error">
            There was an error trying to order to par. You can try again. If the
            problem persists please contact support.
          </Alert>
        ) : null}
        {showCongrats ? (
          <Box>
            <Typography variant="h2" sx={{ marginTop: 6 }}>
              Congratulations!
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ marginTop: 6, fontWeight: 700 }}
            >
              You have successfully submitted the count {stockCount?.name}
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            width: "100%",
            height: "68px",
            display: "flex",
            alignItems: "center",
            marginTop: 6,
            backgroundColor: (theme) => theme.palette.pure.white,
            border: "1px solid",
            borderColor: (theme) => theme.palette.terrain[300],
            borderRadius: 2,
          }}
        >
          <Grid
            container
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 2,
            }}
          >
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total Value on Hand:
              </Typography>
            </Grid>
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle2">
                {currencySymbol}
                {utilFunctions.convertCentsToLargerFormatCurrency(
                  stockCount.finalTotalValue?.amount || 0,
                  locale,
                  currency
                )}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ marginTop: 6 }}>
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
            }}
          >
            {/* <Grid item>
              <TextField
                fullWidth
                size="small"
                select
                autoComplete="off"
                sx={{
                  height: "34px",
                  width: "200px",
                  backgroundColor: (theme) => theme.palette.pure.white,
                  "& legend": { display: "none" },
                  "& fieldset": { top: 0 },
                }}
              >
                <MenuItem>Product Summary</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                sx={{
                  marginLeft: 2,
                  border: "2px solid",
                  borderColor: (theme) => theme.palette.primary.main,
                }}
              >
                <TableViewOutlinedIcon sx={{ fontSize: 20, marginRight: 1 }} />
                <Typography variant="largeButton">Group</Typography>
              </Button>
              
              <Button
                variant="outlined"
                sx={{
                  marginLeft: 2,
                  border: "2px solid",
                  borderColor: (theme) => theme.palette.primary.main,
                }}
              >
                <FilterListOutlinedIcon sx={{ fontSize: 20, marginRight: 1 }} />
                <Typography variant="largeButton">Sort</Typography>
              </Button>
            </Grid> */}
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="outlined"
                sx={{
                  marginRight: 2,
                  border: "2px solid",
                  borderColor: (theme) => theme.palette.primary.main,
                }}
                onClick={handleAddProductModalOpen}
              >
                <Typography variant="largeButton">Add Product</Typography>
              </Button>
              <Button
                variant="outlined"
                sx={{
                  marginRight: 2,
                  border: "2px solid",
                  borderColor: (theme) => theme.palette.primary.main,
                }}
                onClick={handleOpenSortMenu}
              >
                <TuneOutlinedIcon sx={{ fontSize: 20, marginRight: 1 }} />
                <Typography variant="largeButton">Filter</Typography>
              </Button>
              <Menu
                anchorEl={sortMenuAnchor}
                open={!!sortMenuAnchor}
                onClose={handleCloseSortMenu}
              >
                <MenuItem
                  sx={{ margin: "5px" }}
                  selected={filter === "underPar"}
                  id="filter-under"
                  onClick={setUnderFilter}
                >
                  Under Par
                </MenuItem>
                <MenuItem
                  sx={{ margin: "5px" }}
                  selected={filter === "noPar"}
                  id="filter-no-par"
                  onClick={setNoParFilter}
                >
                  No Par Set
                </MenuItem>
              </Menu>
              <Button variant="contained" onClick={handleOrderToPar} disabled={orderToParMutation.isLoading}>
                {orderToParMutation?.isLoading ? <CircularProgress size={24} color="secondary" /> : <Typography variant="largeButton">Order to Par</Typography>}
              </Button>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ marginTop: 6 }}>
          <CountProductListTable
            records={
              filter != false
                ? stockCount?.stockCountRecords.filter(
                    (x) =>
                      x.finalParStatus ===
                      (filter === "noPar"
                        ? inventoryObjects.parStatusTypes.unknown
                        : inventoryObjects.parStatusTypes.under)
                  )
                : stockCount?.stockCountRecords
            }
            openRecordCostSetModal={handleCostsModalOpen}
            toggleParsModalOpen={handleParsModalOpen}
            openEditQuantityModal={handleEditQuantityModalOpen}
          />
        </Box>
        <CreateMissingCostsModal
          headerText="Missing Costs"
          buttonText="Save Details"
          isOpen={costsModalOpen}
          record={recordForCostSetting}
          closeModal={handleClostCostModal}
        />
        <MissingParLevelsModal
          headerText="Missing Par Levels"
          buttonText="Save Pars"
          isOpen={parsModalOpen}
          toggleModalOpen={handleParsModalOpen}
        />
        <OrderToParConfirmationModal
          isOpen={showOrderConfirmationModal || noProductsUnderPar}
          showNoUnderPar={noProductsUnderPar}
          closeModal={handleCloseOrderToParModal}
          loading={orderToParMutation.isLoading}
          orderToParData={confirmationData}
          orderToParMutation={orderToParMutation}
          orderCallback={handleOrderToPar}
        />
        <OrderToParSuccessModal
          isOpen={showSuccessOrderToParModal}
          closeModal={() => {
            toggleSuccessOrderToParModal(false);
          }}
          establishmentId={establishmentId}
        />
        <OrderToParNoValidOrdersModal
          isOpen={showNoValidOrdersModal}
          closeModal={() => {
            toggleNoValidOrdersModal(false);
          }}
          orderToParData={confirmationData}
        />
        <EditStockCountRecordQuantityModal
          isOpen={editQuantityModalOpen}
          handleCloseEditQuantityModal={handleCloseEditQuantityModal}
          record={recordForQuantityEdit}
          stockCountId={stockCountId}
          establishmentId={establishmentId}
          onSuccessCallback={() => {
            setAlertSuccess("Quantity updated successfully!");
          }}
        />
        <AddProductToCompletedStockCountModal
          isOpen={addProductModalOpen}
          handleCloseAddProductModal={handleCloseAddProductModal}
          stockCountId={stockCountId}
          establishmentId={establishmentId}
          onSuccessCallback={() => {
            // Refetch the stock count data
            queryClient.invalidateQueries(["stock-count", { establishmentId, stockCountId }]);
            queryClient.refetchQueries(["stock-count", { establishmentId, stockCountId }]);
            // Show success toast
            setAlertSuccess("Products added successfully!");
          }}
        />
      </Grid>
      <SnackBarSuccess
        alertSuccess={alertSuccess}
        setAlertSuccess={setAlertSuccess}
        durationLength={3000}
      />
    </>
  );
};

export default StockCountReview;
